import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  History, RefreshCw, Loader2, Link2, ShieldCheck, ArrowRight, Archive, Download,
  Lightbulb, Scale, Handshake, Heart, Quote, CircleCheck, Lock, LockOpen, User, MonitorSmartphone,
  Fingerprint, KeyRound, CalendarClock, GitBranch,
} from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";
import { useNexsysGate } from "./gate";

const CLASS_META = {
  DECISION: { icon: Lightbulb, tone: "cyan", color: "#22d3ee" },
  CONSTRAINT: { icon: Scale, tone: "amber", color: "#f59e0b" },
  COMMITMENT: { icon: Handshake, tone: "magenta", color: "#ec4899" },
  PREFERENCE: { icon: Heart, tone: "emerald", color: "#34d399" },
  ASSERTION: { icon: Quote, tone: "indigo", color: "#818cf8" },
};

const fmt = (t) => (t || "").slice(0, 16).replace("T", " ");

function SectionLabel({ children }) {
  return <div className="pt-1 font-mono text-[10px] tracking-[0.3em] text-cyan-300/60">{children}</div>;
}

function ObjectRow({ o, isAdmin, onHold, holding }) {
  const meta = CLASS_META[o.object_class] || CLASS_META.DECISION;
  const Icon = meta.icon;
  const held = !!o.retention?.legal_hold;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-testid="continuum-object-row">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: meta.color }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[13px] font-medium text-white/90">{o.title || "—"}</span>
          {held && <Pill tone="amber" dot={false}>LEGAL HOLD</Pill>}
        </div>
        <div className="mt-1 font-mono text-[10px] text-white/35">
          {o.origin === "continuum-signal" ? "signal-filtered" : "orchestrated"} · sig {o.significance ?? "—"} ·{" "}
          {fmt(o.created_at)} · retain {o.retention?.baseline_years ?? 7}y
          {o.expires_at ? ` · expires ${String(o.expires_at).slice(0, 10)}` : ""}
          {held && o.retention?.hold_set_by ? ` · hold by ${o.retention.hold_set_by.split("@")[0]}` : ""}
        </div>
      </div>
      {isAdmin && (
        <button
          onClick={() => onHold(o.id, !held)}
          disabled={holding === o.id}
          title={held ? "Release legal hold" : "Place legal hold"}
          className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 font-mono text-[10px] transition-colors ${
            held
              ? "border-amber-400/40 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
              : "border-white/10 text-white/40 hover:border-amber-400/40 hover:text-amber-300"
          }`}
          data-testid={`continuum-hold-toggle-${o.id}`}
        >
          {holding === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : held ? <Lock className="h-3 w-3" /> : <LockOpen className="h-3 w-3" />}
          {held ? "HELD" : "HOLD"}
        </button>
      )}
    </div>
  );
}

function ClassGroup({ cls, objs, isAdmin, onHold, holding }) {
  const meta = CLASS_META[cls];
  const Icon = meta.icon;
  return (
    <div data-testid={`continuum-class-group-${cls.toLowerCase()}`}>
      <div className="flex items-center gap-2 px-1 pb-2">
        <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
        <span className="font-mono text-[10px] tracking-[0.2em] text-white/45">{cls}</span>
        <span className="rounded-full border border-white/10 px-1.5 font-mono text-[9px] text-white/40">{objs.length}</span>
      </div>
      <div className="space-y-2">
        {objs.map((o) => <ObjectRow key={o.id} o={o} isAdmin={isAdmin} onHold={onHold} holding={holding} />)}
      </div>
    </div>
  );
}

function GovRow({ icon: Icon, label, children, color = "#22d3ee" }) {
  return (
    <div className="flex items-start gap-3 border-b border-white/[0.05] px-4 py-3 last:border-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">{label}</div>
        <div className="mt-1 font-mono text-[11px] leading-relaxed text-white/60">{children}</div>
      </div>
    </div>
  );
}

export function Continuum() {
  const { identity } = useNexsysGate();
  const isAdmin = !!identity?.is_admin;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [resuming, setResuming] = useState(false);
  const [trace, setTrace] = useState(null);
  const [holding, setHolding] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    nexsysApi.continuumMe().then(setData).catch(() => setErr("Continuum service unavailable"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runResume = async () => {
    setResuming(true);
    setTrace(null);
    try {
      const r = await nexsysApi.continuumResume();
      setTrace(r.trace || []);
      load();
    } catch { setErr("Resumption failed"); }
    finally { setResuming(false); }
  };

  const toggleHold = async (id, hold) => {
    setHolding(id);
    try {
      await nexsysApi.continuumHold(id, hold);
      load();
    } catch { /* admin-only; ignore */ }
    finally { setHolding(null); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const payload = await nexsysApi.continuumExport();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `continuum-audit-${(payload.entity?.id || "user").split("@")[0]}-${payload.exported_at?.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* admin-only */ }
    finally { setExporting(false); }
  };

  if (err) return <div className="p-6 font-mono text-xs text-rose-300" data-testid="continuum-error">{err}</div>;
  if (!data) return (
    <div className="flex items-center gap-2 p-6 font-mono text-xs text-white/40" data-testid="continuum-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Resolving Continuum State Object…
    </div>
  );

  const objs = data.objects || [];
  const byClass = data.by_class || {};
  const deltas = data.deltas || [];
  const cso = data.cso || {};
  const sessions = data.session_csos || [];

  return (
    <div className="space-y-5" data-testid="continuum-view">
      {/* ================= header ================= */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <History className="h-5 w-5 text-cyan-300" />
          <h1 className="font-display text-xl font-semibold tracking-tight text-white">Continuum</h1>
          <Pill tone="cyan" dot={false}>GOVERNED PERSISTENCE + STATE RESUMPTION</Pill>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={doExport}
              disabled={exporting}
              className="flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-[12px] font-medium text-white/70 transition-colors hover:border-cyan-400/40 hover:text-cyan-300 disabled:opacity-50"
              data-testid="continuum-export-btn"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Audit Export
            </button>
          )}
          <button
            onClick={runResume}
            disabled={resuming}
            className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-[12px] font-medium text-cyan-300 transition-colors hover:bg-cyan-400/20 disabled:opacity-50"
            data-testid="continuum-resume-btn"
          >
            {resuming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Run Governed Resumption
          </button>
        </div>
      </div>

      {/* ================= CURRENT CSO ================= */}
      <SectionLabel>CURRENT CSO</SectionLabel>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="nx-panel rounded-xl px-4 py-3" data-testid="continuum-cso-entity">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/40"><User className="h-3 w-3" /> ENTITY</div>
          <div className="mt-1 truncate text-[13px] font-medium text-white/90">user:{data.entity?.id}</div>
          <div className="font-mono text-[10px] text-white/35">{data.entity?.name}</div>
        </div>
        <div className="nx-panel rounded-xl px-4 py-3" data-testid="continuum-cso-state">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/40"><GitBranch className="h-3 w-3" /> STATE / VERSION</div>
          <div className="mt-1 text-[13px] font-medium text-white/90">CSO {String(cso.id || "").slice(0, 8)} · v{cso.version}</div>
          <div className="font-mono text-[10px] text-white/35">{(cso.object_refs || []).length} obj · {(cso.delta_refs || []).length} delta · {(cso.linked_csos || []).length} linked</div>
        </div>
        <div className="nx-panel rounded-xl px-4 py-3" data-testid="continuum-cso-resumed">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/40"><CalendarClock className="h-3 w-3" /> LAST RESUMED</div>
          <div className="mt-1 text-[13px] font-medium text-white/90">{cso.last_resumed_at ? fmt(cso.last_resumed_at) : "never"}</div>
          <div className="font-mono text-[10px] text-white/35">UTC</div>
        </div>
        <div className="nx-panel rounded-xl px-4 py-3" data-testid="continuum-cso-auth">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/40"><KeyRound className="h-3 w-3" /> AUTHORIZATION</div>
          <div className="mt-1"><Pill tone="emerald" className="text-[10px]">AUTHORIZED</Pill></div>
          <div className="mt-1 font-mono text-[10px] text-white/35">session {String(data.authorization?.session_id || "").slice(0, 8)}</div>
        </div>
      </div>

      {/* ================= REFERENCED EDIOS STATE ================= */}
      <SectionLabel>REFERENCED EDIOS STATE</SectionLabel>
      <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
        <Panel
          title="Decision Objects"
          subtitle="Resolved by reference from enterprise memory — typed via object_class"
          testid="continuum-objects-panel"
          right={<Pill tone="indigo" dot={false}>{objs.length} refs</Pill>}
        >
          <div className="nx-scroll max-h-[460px] space-y-4 overflow-y-auto p-4">
            {objs.length === 0 && (
              <div className="px-2 py-6 text-center font-mono text-[11px] text-white/35" data-testid="continuum-empty">
                No governed state persisted yet. Significant interactions and orchestrated Decision Objects will be referenced here.
              </div>
            )}
            {Object.keys(CLASS_META)
              .filter((c) => (byClass[c] || 0) > 0)
              .map((c) => (
                <ClassGroup key={c} cls={c} objs={objs.filter((o) => (o.object_class || "DECISION") === c)}
                            isAdmin={isAdmin} onHold={toggleHold} holding={holding} />
              ))}
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel title="Deltas" subtitle="An outcome is not necessarily a Delta — validated on observed state change" testid="continuum-deltas-panel">
            <div className="nx-scroll max-h-[220px] space-y-2 overflow-y-auto p-4">
              {deltas.length === 0 && (
                <div className="py-3 text-center font-mono text-[11px] text-white/35">No lifecycle transitions referenced yet.</div>
              )}
              {deltas.map((d, i) => (
                <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2" data-testid="continuum-delta-row">
                  <div className="flex items-center gap-2 font-mono text-[10px] text-white/55">
                    <span className="truncate text-white/75">{d.title || d.decision_id?.slice(0, 8)}</span>
                    <span className="text-white/35">{d.from_status}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-cyan-300" />
                    <span className="text-cyan-300">{d.to_status}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 font-mono text-[9px] text-white/30">
                    <span>{d.actor?.split("@")[0]} · {fmt(d.at)}</span>
                    <span className={`rounded-full border px-1.5 py-px ${d.validation === "validated" ? "border-emerald-400/30 text-emerald-300/80" : "border-amber-400/30 text-amber-300/80"}`}>
                      {d.validation === "validated" ? "VALIDATED" : "PENDING OBSERVATION"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Related CSOs" subtitle="Linked by reference — never merged" testid="continuum-related-panel">
            <div className="space-y-2 p-4">
              {data.enterprise ? (
                <div className="flex items-center gap-3 rounded-lg border border-indigo-400/20 bg-indigo-500/[0.05] px-3 py-2.5" data-testid="continuum-enterprise-cso">
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-indigo-300" />
                  <div className="min-w-0 font-mono text-[10px] leading-relaxed text-white/55">
                    <span className="text-white/85">enterprise:nexis</span> · v{data.enterprise.version} · {data.enterprise.object_refs} obj · {data.enterprise.delta_refs} delta
                  </div>
                </div>
              ) : (
                <div className="font-mono text-[11px] text-white/35">Enterprise CSO not yet instantiated.</div>
              )}
              {sessions.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="continuum-session-cso">
                  <MonitorSmartphone className="h-3.5 w-3.5 shrink-0 text-cyan-300/70" />
                  <div className="min-w-0 flex-1 font-mono text-[10px] text-white/55">
                    session:{String(s.entity_id || "").slice(0, 8)} · v{s.version} · {s.object_refs} obj · {fmt(s.updated_at)}
                  </div>
                  {s.current && <Pill tone="cyan" dot={false} className="text-[9px]">CURRENT</Pill>}
                </div>
              ))}
              {sessions.length === 0 && <div className="font-mono text-[10px] text-white/30">No session CSOs yet.</div>}
            </div>
          </Panel>
        </div>
      </div>

      {/* ================= GOVERNANCE ================= */}
      <SectionLabel>GOVERNANCE</SectionLabel>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Provenance · Access · Temporal Validity" testid="continuum-governance-panel">
          <div>
            <GovRow icon={Fingerprint} label="PROVENANCE" color="#818cf8">
              Every referenced object carries source, actor, session and capture time. Signal-filtered objects
              originate from governed NexSys interactions; orchestrated objects from the Decision Orchestra.
            </GovRow>
            <GovRow icon={KeyRound} label="ACCESS" color="#22d3ee">
              CSO resolution requires a live, unrevoked gate session ({String(data.authorization?.session_id || "").slice(0, 8)}).
              Legal hold and audit export are admin-only. Personal and enterprise state governed independently.
            </GovRow>
            <GovRow icon={CalendarClock} label="TEMPORAL VALIDITY" color="#f59e0b">
              Baseline retention {cso.retention?.baseline_years ?? 7}y · COMMITMENT/CONSTRAINT 10y · expired
              COMMITMENTs excluded at resumption · legal hold overrides expiration.
              {" "}Held objects: <span className="text-amber-300">{objs.filter((o) => o.retention?.legal_hold).length}</span>
            </GovRow>
            <GovRow icon={ShieldCheck} label="SIGNAL PERSISTENCE CONFIG" color="#34d399">
              Significance threshold ≥ {data.config?.significance_threshold}/100 — governed/configurable
              (prototype default, not an architectural invariant).
            </GovRow>
          </div>
        </Panel>

        <Panel title="Audit / Lineage" subtitle="Governed State Resumption — 8-step flow" testid="continuum-trace-panel"
               right={<Archive className="h-4 w-4 text-white/30" />}>
          <div className="p-4">
            {(trace || data.trace || []).map((t, i, arr) => (
              <motion.div
                key={t.step}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                className="relative flex gap-3 pb-3 last:pb-0"
                data-testid={`continuum-trace-step-${i}`}
              >
                <div className="flex flex-col items-center">
                  <CircleCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                  {i < arr.length - 1 && <span className="mt-1 w-px flex-1 bg-white/10" />}
                </div>
                <div className="min-w-0 -mt-0.5">
                  <div className="text-[12px] font-medium text-white/85">{t.step}</div>
                  <div className="font-mono text-[10px] leading-relaxed text-white/40">{t.detail}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
